import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Participant, SourceData, SourceDataEventType, SourceDataFieldType, SourceDataItem, SourceDataItemGroupType, Trial } from "@/api/dtos";
import { SourceDataItemMapping } from "@/components/forms/forms/source-data/values/Index";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useItemGroupConfiguration from "@/hooks/form/configuration/useItemGroupConfiguration";
import useItem from "@/hooks/form/source-data/useItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Divider, ListItem, ListItemIcon, ListItemText, Tooltip, alpha, styled } from "@mui/material";
import { FC } from "react";

const StyledListItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(6)
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

const RepeatBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    whiteSpace: 'pre',
    minHeight: theme.spacing(5.25),
    minWidth: theme.spacing(10.5),
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.mode === 'dark' ?
        alpha(theme.palette.common.white, 0.12):
        alpha(theme.palette.common.black, 0.12),
    background: theme.palette.mode === 'dark' ?
        theme.palette.grey[800] :
        theme.palette.grey[100],

    '& > *': {
        minWidth: theme.spacing(5.25),
        textAlign: 'center'
    }
}))

const RepeatIconBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 1),
    alignSelf: 'stretch',
    textAlign: 'center',
    minWidth: theme.spacing(5.25),
    borderStyle: 'solid',
    borderWidth: 0,
    borderRightWidth: 1,
    borderRadius: 0,
    borderColor: theme.palette.mode === 'dark' ?
        alpha(theme.palette.common.white, 0.12):
        alpha(theme.palette.common.black, 0.12),
    background: theme.palette.mode === 'dark' ?
        theme.palette.grey[700] :
        theme.palette.grey[200]
}))

export interface ISourceDataDetailsProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
    itemValue?: string;

    trial?: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant?: Participant;

    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;

    hideTrial?: boolean;
    hideMasterGroup?: boolean;
    hideCoordinatingGroup?: boolean;
    hideInstitution?: boolean;
    hideParticipant?: boolean;
    hideStudyEvent?: boolean;
    hideCaseReportForm?: boolean;
    hideItemGroup?: boolean;
    hideItem?: boolean;
}


const SourceDataDetails: FC<ISourceDataDetailsProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,
    itemValue,

    trials,
    masterGroups,
    coordinatingGroups,
    institutions,
    participants,
    metadata,
    trialConfiguration,
    configuration,
    sourceData,

    hideTrial,
    hideMasterGroup,
    hideCoordinatingGroup,
    hideInstitution,
    hideParticipant,
    hideStudyEvent,
    hideCaseReportForm,
    hideItemGroup,
    hideItem,
    
    ...props
}) => {
    const trial = props.trial ?? (trialId ? trials?.[trialId] : undefined);
    const masterGroup = props.masterGroup ?? (masterGroupId ? masterGroups?.[masterGroupId] : undefined);
    const coordinatingGroup = props.coordinatingGroup ?? (coordinatingGroupId ? coordinatingGroups?.[coordinatingGroupId] : undefined);
    const institution = props.institution ?? (institutionId ? institutions?.[institutionId] : undefined);
    const participant = props.participant ?? (participantId ? participants?.[participantId] : undefined);

    const studyEvent = useEventConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        studyEventOid
    );

    const caseReportForm = useFormConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        studyEventOid,
        caseReportFormOid
    );

    const itemGroup = useItemGroupConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        studyEventOid,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName
    );

    // const item = useItemConfiguration(
    //     metadata,
    //     trialConfiguration,
    //     configuration ?? trialConfiguration,
    //     trial,
    //     (configuration ?? trialConfiguration)?.configuration?.id,
    //     studyEventOid,
    //     caseReportFormOid,
    //     caseReportFormVersionOid,
    //     itemGroupName,
    //     itemName
    // );

    const item = useItem(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        sourceData ?? new SourceData({
            date: undefined,
            events: undefined,
            forms: undefined,
            items: {
                [studyEventOid!]: {
                    [studyEventRepeat!]: {
                        [caseReportFormOid!]: {
                            [itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]: {
                                [itemGroupRepeat!]: {
                                    [itemName!]: new SourceDataItem({
                                        value: itemValue
                                    })
                                }
                            }
                        }
                    }
                }
            }
        }),
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName
    )

    const ValueComponent = SourceDataItemMapping[item?.itemMetadata.fieldType ?? SourceDataFieldType.Text];

    return (
        <>
            {
                trial && !hideTrial && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Trial"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.trial} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={trial.name}
                            />
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                masterGroup && !hideMasterGroup && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Master Group"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.masterGroup} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={masterGroup.name}
                            />
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                coordinatingGroup && !hideCoordinatingGroup && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Coordinating Group"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.coordinatingGroup} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={coordinatingGroup.name}
                            />
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                institution && !hideInstitution && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Institution"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.institution} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={institution.name}
                            />
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                participant && !hideParticipant && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Participant"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.participant} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={participant.studyNumber}
                            />
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                studyEvent && !hideStudyEvent && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Study Event"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.event} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={studyEvent.displayName}
                            />
                            {
                                studyEvent.eventMetadata.type === SourceDataEventType.Repeating && (
                                    <Tooltip title={<>Event Repeat &mdash; {studyEventRepeat}</>}>
                                        <RepeatBox>
                                            <RepeatIconBox>
                                                <FontAwesomeIcon icon={AppSettings.icons.studyEventRepeat} size="lg" />
                                            </RepeatIconBox>
                                            <Box>
                                                {studyEventRepeat}
                                            </Box>
                                        </RepeatBox>
                                    </Tooltip>
                                )
                            }
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                caseReportForm && !hideCaseReportForm && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Case Report Form"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.form} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={caseReportForm.displayName}
                            />
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                itemGroup && !hideItemGroup && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Item Group"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={itemGroup.itemGroupMetadata.type === SourceDataItemGroupType.Grid ? AppSettings.icons.itemGrid : AppSettings.icons.itemGroup} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={itemGroup.displayName}
                            />
                            {
                                itemGroup.itemGroupMetadata.type === SourceDataItemGroupType.Grid && (
                                    <Tooltip title={<>Row Number &mdash; {itemGroupRepeat}</>}>
                                        <RepeatBox>
                                            <RepeatIconBox>
                                                <FontAwesomeIcon icon={AppSettings.icons.itemGroupRepeat} size="lg" />
                                            </RepeatIconBox>
                                            <Box>
                                                {itemGroupRepeat}
                                            </Box>
                                        </RepeatBox>
                                    </Tooltip>
                                )
                            }
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                item && !hideItem && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Item"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.item} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <StyledListItemText
                                primary={item.displayName}
                            />
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
            {
                item && !hideItem && (
                    <>
                        <StyledListItem>
                            <Tooltip
                                title="Item Value"
                            >
                                <StyledListItemIcon>
                                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.itemValue} />
                                </StyledListItemIcon>
                            </Tooltip>
                            <ValueComponent itemData={item} hideBorderStyles={true}  />
                        </StyledListItem>
                        <Divider />
                    </>
                )
            }
        </>
    )
}

export default SourceDataDetails;